import { PageHeader, DataTable, LoadingState, EmptyState } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Container,
  Avatar,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Grid,
  ListSubheader,
  useMediaQuery,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Divider
} from '@mui/material';
import { Edit, Delete, School, Group, Subject as SubjectIcon, Class as ClassIcon, CallMerge } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../utils/Api';

// 🎯 Memoized constants and configuration
const ALLOWED_CLASS_NAMES = Object.freeze([
  { label: 'CRECHE', group: 'creche' },
  { label: 'NURSERY 1', group: 'Nursery' },
  { label: 'NURSERY 2', group: 'Nursery' },
  { label: 'KG 1', group: 'Kindergarten' },
  { label: 'KG 2', group: 'Kindergarten' },
  { label: 'BASIC 1', group: 'Basic' },
  { label: 'BASIC 2', group: 'Basic' },
  { label: 'BASIC 3', group: 'Basic' },
  { label: 'BASIC 4', group: 'Basic' },
  { label: 'BASIC 5', group: 'Basic' },
  { label: 'BASIC 6', group: 'Basic' },
  { label: 'BASIC 7', group: 'Basic' },
  { label: 'BASIC 8', group: 'Basic' },
  { label: 'BASIC 9', group: 'Basic' },
  { label: 'GRADE 1', group: 'Grade' },
  { label: 'GRADE 2', group: 'Grade' },
  { label: 'GRADE 3', group: 'Grade' },
  { label: 'GRADE 4', group: 'Grade' },
  { label: 'GRADE 5', group: 'Grade' },
  { label: 'GRADE 6', group: 'Grade' },
  { label: 'GRADE 7', group: 'Grade' },
  { label: 'GRADE 8', group: 'Grade' },
  { label: 'GRADE 9', group: 'Grade' },
]);

// 🎯 Memoized mobile card component
const MobileClassCard = memo(({ classItem, onEdit, onDelete, theme }) => {
  const getInitials = useCallback((className) => {
    const words = className.split(' ');
    if (words.length === 1) return className.substring(0, 2).toUpperCase();
    return words.map(word => word[0]).join('').toUpperCase();
  }, []);

  const getClassColor = useCallback((className) => {
    const colors = ['primary', 'secondary', 'success', 'warning', 'info', 'error'];
    const index = className.length % colors.length;
    return colors[index];
  }, []);

  const teachers = useMemo(() =>
    Array.isArray(classItem.teachers) ? classItem.teachers : [],
    [classItem.teachers]
  );

  return (
    <Card sx={{ mb: 2, boxShadow: 2, border: `1px solid ${theme.palette.divider}`, transition: 'background-color 0.5s' }} id={classItem._id}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{
                bgcolor: theme.palette.primary.main,
                width: 40,
                height: 40
              }}>
                {getInitials(classItem.displayName || classItem.name)}
              </Avatar>
              <Typography variant="subtitle1" fontWeight="600">
                {classItem.displayName || classItem.name}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit Class">
                <IconButton
                  onClick={() => onEdit(classItem)}
                  color="primary"
                  size="small"
                  sx={{
                    minWidth: 32,
                    minHeight: 32,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Class">
                <IconButton
                  onClick={() => onDelete(classItem._id)}
                  color="error"
                  size="small"
                  sx={{
                    minWidth: 32,
                    minHeight: 32,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              TEACHERS & SUBJECTS
            </Typography>
            {teachers.length > 0 ? (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {teachers.slice(0, 2).map((teacher, index) => (
                  <Chip
                    key={teacher._id || index}
                    label={teacher.name || 'Unknown Teacher'}
                    size="small"
                    color={getClassColor(teacher.name)}
                    variant="outlined"
                    sx={{ mb: 0.5 }}
                  />
                ))}
                {teachers.length > 2 && (
                  <Chip
                    label={`+${teachers.length - 2} more`}
                    size="small"
                    color="default"
                    variant="outlined"
                    sx={{ mb: 0.5 }}
                  />
                )}
              </Stack>
            ) : (
              <Chip
                label="No teachers"
                size="small"
                color="default"
                variant="outlined"
                sx={{ mb: 0.5 }}
              />
            )}
            
            {classItem.subjects && classItem.subjects.length > 0 && (
              <Typography variant="caption" color="primary" display="block" sx={{ mt: 1 }}>
                {classItem.subjects.length} Subjects Assigned
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
});

// 🎯 Memoized table row component
const ClassTableRow = memo(({ classItem, onEdit, onDelete, theme }) => {
  const getInitials = useCallback((className) => {
    const words = className.split(' ');
    if (words.length === 1) return className.substring(0, 2).toUpperCase();
    return words.map(word => word[0]).join('').toUpperCase();
  }, []);

  const getClassColor = useCallback((className) => {
    const colors = ['primary', 'secondary', 'success', 'warning', 'info', 'error'];
    const index = className.length % colors.length;
    return colors[index];
  }, []);

  const teachers = useMemo(() =>
    Array.isArray(classItem.teachers) ? classItem.teachers : [],
    [classItem.teachers]
  );

  return (
    <TableRow
      id={classItem._id}
      sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) }, transition: 'background-color 0.5s' }}
    >
      <TableCell sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{
            bgcolor: theme.palette.primary.main,
            width: 40,
            height: 40
          }}>
            {getInitials(classItem.displayName || classItem.name)}
          </Avatar>
          <Typography variant="subtitle1" fontWeight="500">
            {classItem.displayName || classItem.name}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ py: 2 }}>
        {teachers.length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {teachers.slice(0, 3).map((teacher, index) => (
              <Chip
                key={teacher._id || index}
                label={teacher.name || 'Unknown Teacher'}
                size="small"
                color={getClassColor(teacher.name)}
                variant="outlined"
                sx={{ mb: 0.5 }}
              />
            ))}
            {teachers.length > 3 && (
              <Chip
                label={`+${teachers.length - 3} more`}
                size="small"
                color="default"
                variant="outlined"
                sx={{ mb: 0.5 }}
              />
            )}
          </Stack>
        ) : (
          <Chip
            label="No teachers"
            size="small"
            color="default"
            variant="outlined"
            sx={{ mb: 0.5 }}
          />
        )}
        
        {classItem.subjects && classItem.subjects.length > 0 && (
          <Typography variant="caption" color="primary" display="block" sx={{ mt: 1 }}>
            {classItem.subjects.length} Subjects Assigned
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ py: 2, textAlign: 'center' }}>
        <Stack direction="row" spacing={1} justifyContent="center">
          <Tooltip title="Edit Class">
            <IconButton
              onClick={() => onEdit(classItem)}
              color="primary"
              size="small"
              sx={{
                minWidth: 32,
                minHeight: 32,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Class">
            <IconButton
              onClick={() => onDelete(classItem._id)}
              color="error"
              size="small"
              sx={{
                minWidth: 32,
                minHeight: 32,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                },
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
});

// 🎯 Main Component with Performance Optimizations
const ClassesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // 🎯 State with useMemo for derived values
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentClass, setCurrentClass] = useState(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [stream, setStream] = useState('');
  
  const [allSubjects, setAllSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkClassIds, setBulkClassIds] = useState([]);
  const [bulkSubjectIds, setBulkSubjectIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState('');

  // 🔀 Merge Streams State
  const [mergeOpen, setMergeOpen] = useState(false);
  const [selectedBaseLevel, setSelectedBaseLevel] = useState('');
  const [mergeSourceIds, setMergeSourceIds] = useState([]);
  const [targetMode, setTargetMode] = useState('base'); // 'base' | 'custom'
  const [customTargetId, setCustomTargetId] = useState('');
  const [deleteSourceAfterMerge, setDeleteSourceAfterMerge] = useState(true);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState('');

  // 🎯 Memoized grouped options for dropdown
  const groupedOptions = useMemo(() =>
    ALLOWED_CLASS_NAMES.reduce((groups, item) => {
      const group = groups[item.group] || [];
      group.push(item);
      groups[item.group] = group;
      return groups;
    }, {}),
    []
  );

  const fetchClasses = useCallback(async () => {
    try {
      setFetching(true);
      const res = await api.get('/classes');
      setClasses(res.data.classes || []);
    } catch (err) {
      console.error('Error fetching classes:', err);
      toast.error('Failed to load classes');
      setClasses([]);
    } finally {
      setFetching(false);
    }
  }, []);

  const fetchSubjects = useCallback(async () => {
    try {
      const res = await api.get('/subjects');
      setAllSubjects(res.data.data || res.data || []);
    } catch (err) {
      console.error('Error fetching subjects:', err);
    }
  }, []);

  // 🎯 Optimized useEffect with proper dependencies
  useEffect(() => {
    fetchClasses();
    fetchSubjects();
  }, [fetchClasses, fetchSubjects]);

  // 🎯 Helper to scroll to element
  const scrollToElement = (id) => {
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.style.transition = 'background-color 2s';
        element.style.backgroundColor = alpha(theme.palette.primary.main, 0.2);
        setTimeout(() => {
          element.style.backgroundColor = '';
        }, 2000);
      }
    }, 500);
  };

  // 🎯 Handle Deep Linking
  useEffect(() => {
    if (!fetching && classes.length > 0) {
      const params = new URLSearchParams(window.location.search);
      const searchTerm = params.get('search');

      if (searchTerm) {
        const target = classes.find(c =>
          c.name === searchTerm ||
          c.displayName === searchTerm ||
          c._id === searchTerm
        );

        if (target) {
          scrollToElement(target._id);
        }
      }
    }
  }, [fetching, classes, theme.palette.primary.main]);

  // 🎯 Memoized event handlers
  const handleSubmit = useCallback(async () => {
    setError('');
    if (!name.trim()) {
      toast.error('Please select a class name');
      return;
    }

    setLoading(true);

    const payload = {
      name,
      stream: stream || null,
      subjects: selectedSubjects,
    };

    try {
      if (editMode && currentClass) {
        await api.put(`/classes/${currentClass._id}`, payload);
        toast.success('Class updated successfully');
      } else {
        await api.post('/classes', payload);
        toast.success('Class created successfully');
      }

      setName('');
      setStream('');
      setSelectedSubjects([]);
      setOpen(false);
      setEditMode(false);
      setCurrentClass(null);
      fetchClasses();
    } catch (err) {
      console.error('Error saving class:', err.response?.data || err.message);
      const errorMessage = err.response?.data?.message || 'Failed to save class';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [name, stream, selectedSubjects, editMode, currentClass, fetchClasses]);

  const handleEdit = useCallback((classItem) => {
    setCurrentClass(classItem);
    setName(classItem.name);
    setStream(classItem.stream || '');
    
    // Set selected subjects (extract IDs if populated)
    if (classItem.subjects && Array.isArray(classItem.subjects)) {
      setSelectedSubjects(classItem.subjects.map(s => s._id || s));
    } else {
      setSelectedSubjects([]);
    }
    
    setEditMode(true);
    setOpen(true);
  }, []);

  const handleDelete = useCallback(async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;

    try {
      await api.delete(`/classes/${classId}`);
      toast.success('Class deleted successfully');
      fetchClasses();
    } catch (err) {
      console.error('Error deleting class:', err);
      toast.error('Failed to delete class');
    }
  }, [fetchClasses]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setEditMode(false);
    setCurrentClass(null);
    setName('');
    setStream('');
    setSelectedSubjects([]);
    setError('');
  }, []);

  // 🎯 Compute groups of classes by base name that have streams or multiple sections
  const streamGroups = useMemo(() => {
    const map = {};
    for (const cls of classes) {
      const baseName = (cls.name || '').trim();
      if (!baseName) continue;
      if (!map[baseName]) {
        map[baseName] = [];
      }
      map[baseName].push(cls);
    }

    const result = [];
    for (const [baseName, clsList] of Object.entries(map)) {
      const streamed = clsList.filter((c) => Boolean(c.stream));
      const unstreamed = clsList.find((c) => !c.stream) || null;
      if (streamed.length > 0 || clsList.length > 1) {
        result.push({
          baseName,
          classes: clsList,
          streamClasses: streamed.length > 0 ? streamed : clsList,
          unstreamedClass: unstreamed,
          streams: streamed.map((c) => c.stream),
          totalStudents: clsList.reduce((sum, c) => sum + (c.students?.length || 0), 0),
        });
      }
    }

    return result.sort((a, b) => a.baseName.localeCompare(b.baseName));
  }, [classes]);

  const handleOpenMerge = useCallback(() => {
    setMergeError('');
    if (streamGroups.length > 0) {
      const defaultGroup = streamGroups[0];
      setSelectedBaseLevel(defaultGroup.baseName);
      setMergeSourceIds(defaultGroup.streamClasses.map((c) => c._id));
      setTargetMode('base');
      setCustomTargetId('');
    } else {
      setSelectedBaseLevel('CUSTOM');
      setMergeSourceIds([]);
      setTargetMode('custom');
      setCustomTargetId('');
    }
    setDeleteSourceAfterMerge(true);
    setMergeOpen(true);
  }, [streamGroups]);

  const handleCloseMerge = useCallback(() => {
    if (mergeLoading) return;
    setMergeOpen(false);
    setMergeError('');
    setSelectedBaseLevel('');
    setMergeSourceIds([]);
    setCustomTargetId('');
  }, [mergeLoading]);

  const handleBaseLevelChange = useCallback((level) => {
    setSelectedBaseLevel(level);
    setMergeError('');
    if (level === 'CUSTOM') {
      setMergeSourceIds([]);
      setTargetMode('custom');
      setCustomTargetId('');
    } else {
      const group = streamGroups.find((g) => g.baseName === level);
      if (group) {
        setMergeSourceIds(group.streamClasses.map((c) => c._id));
        setTargetMode('base');
        setCustomTargetId('');
      }
    }
  }, [streamGroups]);

  const handleToggleSourceClass = useCallback((classId) => {
    setMergeSourceIds((prev) =>
      prev.includes(classId) ? prev.filter((id) => id !== classId) : [...prev, classId]
    );
  }, []);

  const handleExecuteMerge = useCallback(async () => {
    setMergeError('');

    if (mergeSourceIds.length === 0) {
      const err = 'Please select at least one stream class to merge.';
      setMergeError(err);
      toast.error(err);
      return;
    }

    let payload = {
      sourceClassIds: mergeSourceIds,
      deleteSourceClasses: deleteSourceAfterMerge,
    };

    if (targetMode === 'base') {
      if (!selectedBaseLevel || selectedBaseLevel === 'CUSTOM') {
        const err = 'Please select a valid base class level.';
        setMergeError(err);
        toast.error(err);
        return;
      }
      payload.targetClassName = selectedBaseLevel;
      payload.targetClassStream = null;
    } else {
      if (!customTargetId) {
        const err = 'Please select a destination target class.';
        setMergeError(err);
        toast.error(err);
        return;
      }
      payload.targetClassId = customTargetId;
    }

    setMergeLoading(true);
    try {
      const res = await api.post('/classes/merge-streams', payload);
      toast.success(res.data?.message || 'Streams merged successfully!');
      setMergeOpen(false);
      fetchClasses();
    } catch (err) {
      console.error('Error merging streams:', err.response?.data || err.message);
      const msg = err.response?.data?.message || 'Failed to merge streams';
      setMergeError(msg);
      toast.error(msg);
    } finally {
      setMergeLoading(false);
    }
  }, [
    mergeSourceIds,
    deleteSourceAfterMerge,
    targetMode,
    selectedBaseLevel,
    customTargetId,
    fetchClasses,
  ]);

  const mergePreview = useMemo(() => {
    const selectedSourceClasses = classes.filter((c) => mergeSourceIds.includes(c._id));
    const totalStudentsToMove = selectedSourceClasses.reduce(
      (sum, c) => sum + (Array.isArray(c.students) ? c.students.length : 0),
      0
    );

    let targetClassDoc = null;
    let targetIsNew = false;
    let targetDisplayName = '';

    if (targetMode === 'base') {
      targetClassDoc = classes.find(
        (c) => c.name === selectedBaseLevel && !c.stream && !mergeSourceIds.includes(c._id)
      );
      targetDisplayName = selectedBaseLevel || 'Unified Class';
      targetIsNew = !targetClassDoc;
    } else {
      targetClassDoc = classes.find((c) => c._id === customTargetId) || null;
      targetDisplayName = targetClassDoc
        ? targetClassDoc.displayName || targetClassDoc.name
        : 'Selected Class';
      targetIsNew = false;
    }

    const existingTargetStudents = targetClassDoc
      ? Array.isArray(targetClassDoc.students)
        ? targetClassDoc.students.length
        : 0
      : 0;

    const finalStudentCount = totalStudentsToMove + existingTargetStudents;

    const allTeacherIds = new Set();
    selectedSourceClasses.forEach((c) => {
      (c.teachers || []).forEach((t) => allTeacherIds.add(String(t._id || t)));
    });
    if (targetClassDoc?.teachers) {
      targetClassDoc.teachers.forEach((t) => allTeacherIds.add(String(t._id || t)));
    }

    const allSubjectIds = new Set();
    selectedSourceClasses.forEach((c) => {
      (c.subjects || []).forEach((s) => allSubjectIds.add(String(s._id || s)));
    });
    if (targetClassDoc?.subjects) {
      targetClassDoc.subjects.forEach((s) => allSubjectIds.add(String(s._id || s)));
    }

    return {
      selectedSourceClasses,
      totalStudentsToMove,
      targetClassDoc,
      targetIsNew,
      targetDisplayName,
      existingTargetStudents,
      finalStudentCount,
      totalTeachersCount: allTeacherIds.size,
      totalSubjectsCount: allSubjectIds.size,
    };
  }, [classes, mergeSourceIds, targetMode, selectedBaseLevel, customTargetId]);

  const handleBulkClose = useCallback(() => {
    if (bulkLoading) return;
    setBulkOpen(false);
    setBulkClassIds([]);
    setBulkSubjectIds([]);
    setBulkError('');
  }, [bulkLoading]);

  const handleBulkAddSubjects = useCallback(async () => {
    setBulkError('');

    if (bulkClassIds.length === 0) {
      setBulkError('Select at least one class.');
      toast.error('Select at least one class.');
      return;
    }

    if (bulkSubjectIds.length === 0) {
      setBulkError('Select at least one subject.');
      toast.error('Select at least one subject.');
      return;
    }

    setBulkLoading(true);

    try {
      const res = await api.patch('/classes/bulk-subjects', {
        classIds: bulkClassIds,
        subjects: bulkSubjectIds,
      });

      toast.success(res.data?.message || 'Subjects added to selected classes.');
      setBulkOpen(false);
      setBulkClassIds([]);
      setBulkSubjectIds([]);
      fetchClasses();
    } catch (err) {
      console.error('Error bulk adding subjects:', err.response?.data || err.message);
      const message = err.response?.data?.message || 'Failed to add subjects to selected classes';
      setBulkError(message);
      toast.error(message);
    } finally {
      setBulkLoading(false);
    }
  }, [bulkClassIds, bulkSubjectIds, fetchClasses]);

  // 🎯 Memoized computed values
  

  const classStats = useMemo(() => {
    const totalClasses = classes.length;
    const classesWithTeachers = classes.filter((classItem) =>
      Array.isArray(classItem.teachers) && classItem.teachers.length > 0
    ).length;
    const subjectAssignments = classes.reduce(
      (total, classItem) => total + (Array.isArray(classItem.subjects) ? classItem.subjects.length : 0),
      0
    );

    return {
      totalClasses,
      classesWithTeachers,
      subjectAssignments,
    };
  }, [classes]);

  const pageHeader = useMemo(() => (
    <PageHeader title="Class Management" description="Create classes, assign subjects, merge streams, and manage teacher assignments." actions={<><Button variant="outlined" startIcon={<CallMerge />} onClick={handleOpenMerge} size={isMobile ? "medium" : "large"} fullWidth={isMobile} disabled={classes.length === 0} sx={{
        fontWeight: 600,
        color: theme.palette.mode === 'dark' ? '#93c5fd' : '#0f172a',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(147, 197, 253, 0.4)' : 'rgba(15, 23, 42, 0.3)',
        '&:hover': {
          borderColor: theme.palette.mode === 'dark' ? '#93c5fd' : '#0f172a',
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(147, 197, 253, 0.08)' : 'rgba(15, 23, 42, 0.06)'
        }
      }}>
                    Merge Streams
                  </Button>
      <Button variant="outlined" startIcon={<SubjectIcon />} onClick={() => setBulkOpen(true)} size={isMobile ? "medium" : "large"} fullWidth={isMobile} disabled={classes.length === 0 || allSubjects.length === 0}>
                    Bulk Add Subjects
                  </Button>
      <Button variant="contained" startIcon={<Group />} onClick={() => setOpen(true)} size={isMobile ? "medium" : "large"} fullWidth={isMobile}>
                    Add New Class
                  </Button></>} />
  ), [isMobile, theme, classes.length, allSubjects.length]);

  const statsCardsContent = useMemo(() => (
    <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <SummaryMetric sx={{
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.primary.main}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: isMobile ? 'none' : 'translateY(-4px)',
            boxShadow: isMobile ? 2 : 6
          }
        }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{
                bgcolor: theme.palette.primary.main,
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48
              }}>
                <ClassIcon fontSize={isMobile ? "small" : "medium"} />
              </Avatar>
              <Box>
                <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary">
                  Total Classes
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                  {classStats.totalClasses}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Classes created
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </SummaryMetric>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <SummaryMetric sx={{
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.info.main}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: isMobile ? 'none' : 'translateY(-4px)',
            boxShadow: isMobile ? 2 : 6
          }
        }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{
                bgcolor: theme.palette.info.main,
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48
              }}>
                <Group fontSize={isMobile ? "small" : "medium"} />
              </Avatar>
              <Box>
                <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary">
                  Assigned Classes
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                  {classStats.classesWithTeachers}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Have teachers assigned
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </SummaryMetric>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <SummaryMetric sx={{
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.success.main}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: isMobile ? 'none' : 'translateY(-4px)',
            boxShadow: isMobile ? 2 : 6
          }
        }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{
                bgcolor: theme.palette.success.main,
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48
              }}>
                <SubjectIcon fontSize={isMobile ? "small" : "medium"} />
              </Avatar>
              <Box>
                <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary">
                  Subject Assignments
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                  {classStats.subjectAssignments}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Across all classes
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </SummaryMetric>
      </Grid>
    </SummaryStrip>
  ), [classStats, isMobile, theme]);

  const emptyState = useMemo(() => (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
      <School sx={{
        fontSize: isMobile ? 48 : 64,
        color: 'text.secondary',
        opacity: 0.5,
        mb: 2
      }} />
      <Typography variant={isMobile ? "h6" : "h6"} color="text.secondary" gutterBottom>
        No classes found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Create your first class to get started
      </Typography>
    </Box>
  ), [isMobile]);

  const mobileClassCards = useMemo(() => (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        {classes.map((classItem) => (
          <MobileClassCard
            key={classItem._id}
            classItem={classItem}
            onEdit={handleEdit}
            onDelete={handleDelete}
            theme={theme}
          />
        ))}
      </Stack>
    </Box>
  ), [classes, handleEdit, handleDelete, theme]);

  const desktopTable = useMemo(() => (
    <Box sx={{
      width: '100%',
      overflow: 'auto',
      maxHeight: isTablet ? '60vh' : 'none'
    }}>
      <DataTable><Table sx={{ minWidth: isTablet ? 600 : 'auto' }}>
        <TableHead sx={{ bgcolor: theme.palette.background.default }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CLASS</TableCell>
            <TableCell sx={{ fontWeight: 'bold', py: 2 }}>TEACHERS</TableCell>
            <TableCell sx={{ fontWeight: 'bold', py: 2, textAlign: 'center' }}>ACTIONS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
{classes.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

          {classes.map((classItem) => (
            <ClassTableRow
              key={classItem._id}
              classItem={classItem}
              onEdit={handleEdit}
              onDelete={handleDelete}
              theme={theme}
            />
          ))}
        </TableBody>
      </Table></DataTable>
    </Box>
  ), [classes, isTablet, theme, handleEdit, handleDelete]);

  const classListContent = useMemo(() => {
    if (fetching) {
      return (
        <LoadingState />
      );
    }

    return (
      <Card sx={{
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            p: isMobile ? 2 : 3,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 1 : 0}
            >
              <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                Class Records ({classes.length})
              </Typography>
              {classes.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: isMobile ? 1 : 0 }}
                >
                  <Chip
                    label={`${classStats.classesWithTeachers} assigned`}
                    color="primary"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                  <Chip
                    label={`${classStats.subjectAssignments} subjects`}
                    color="success"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                  />
                </Stack>
              )}
            </Stack>
          </Box>

          {classes.length === 0 ? emptyState : (
            isMobile ? mobileClassCards : desktopTable
          )}
        </CardContent>
      </Card>
    );
  }, [fetching, isMobile, theme, classes.length, classStats, emptyState, mobileClassCards, desktopTable]);

  const dialogContent = useMemo(() => (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : 2 }
      }}
    >
      <DialogTitle sx={{
        bgcolor: theme.palette.primary.main,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: isMobile ? 2 : 3
      }}>
        <School />
        {editMode ? 'Edit Class' : 'Add New Class'}
      </DialogTitle>

      <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
            {error}
          </Alert>
        )}

        <TextField
          select
          fullWidth
          label="Class Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!error}
          helperText={
            !name ? "Select a class name" :
              editMode && currentClass?.stream ?
                `Display as: ${name}${currentClass.stream}` :
                stream ? `Will display as: ${name}${stream}` : ""
          }
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            renderValue: name !== '' ? undefined : () => 'Select Class Name'
          }}
          size={isMobile ? "small" : "medium"}
        >
          <MenuItem value="" disabled>
            Select Class Name
          </MenuItem>
          {Object.entries(groupedOptions).map(([group, items]) => [
            <ListSubheader
              key={group}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}
            >
              {group.toUpperCase()}
            </ListSubheader>,
            ...items.map(({ label }) => (
              <MenuItem key={label} value={label}>
                {label}
              </MenuItem>
            )),
          ])}
        </TextField>
        <TextField
          select
          fullWidth
          label="Class Stream (Optional)"
          value={stream}
          onChange={(e) => setStream(e.target.value)}
          size={isMobile ? "small" : "medium"}
          sx={{ mt: 2 }}
        >
          <MenuItem value="">No Stream</MenuItem>
          <MenuItem value="A">A</MenuItem>
          <MenuItem value="B">B</MenuItem>
          <MenuItem value="C">C</MenuItem>
        </TextField>
        
        <FormControl fullWidth sx={{ mt: 2 }} size={isMobile ? "small" : "medium"}>
          <InputLabel id="subjects-mutiple-checkbox-label">Assigned Subjects</InputLabel>
          <Select
            labelId="subjects-mutiple-checkbox-label"
            id="subjects-mutiple-checkbox"
            multiple
            value={selectedSubjects}
            onChange={(e) => setSelectedSubjects(e.target.value)}
            input={<OutlinedInput label="Assigned Subjects" />}
            renderValue={(selected) => {
              const count = selected.length;
              return count === 0 ? "None selected" : `${count} Subject${count > 1 ? 's' : ''} Selected`;
            }}
          >
            {allSubjects.map((subject) => (
              <MenuItem key={subject._id} value={subject._id}>
                <Checkbox checked={selectedSubjects.indexOf(subject._id) > -1} />
                <ListItemText primary={subject.name} secondary={subject.shortName || subject.code} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{
        p: isMobile ? 2 : 3,
        gap: 1,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          fullWidth={isMobile}
          size={isMobile ? "medium" : "large"}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          fullWidth={isMobile}
          size={isMobile ? "medium" : "large"}
        >
          {loading ? 'Saving...' : (editMode ? 'Update Class' : 'Create Class')}
        </Button>
      </DialogActions>
    </Dialog>
  ), [open, handleClose, isMobile, theme, editMode, error, name, stream, currentClass, groupedOptions, loading, handleSubmit, selectedSubjects, allSubjects]);

  const bulkDialogContent = useMemo(() => (
    <Dialog
      open={bulkOpen}
      onClose={handleBulkClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : 2 }
      }}
    >
      <DialogTitle sx={{
        bgcolor: theme.palette.primary.main,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: isMobile ? 2 : 3
      }}>
        <SubjectIcon />
        Bulk Add Subjects
      </DialogTitle>

      <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
        {bulkError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
            {bulkError}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the classes once, select the subjects once, then apply them together. Existing subjects on those classes will not be removed.
        </Typography>

        <FormControl fullWidth size={isMobile ? "small" : "medium"}>
          <InputLabel id="bulk-classes-checkbox-label">Classes</InputLabel>
          <Select
            labelId="bulk-classes-checkbox-label"
            id="bulk-classes-checkbox"
            multiple
            value={bulkClassIds}
            onChange={(e) => {
              const value = e.target.value;
              setBulkClassIds(typeof value === 'string' ? value.split(',') : value);
            }}
            input={<OutlinedInput label="Classes" />}
            renderValue={(selected) => {
              const count = selected.length;
              return count === 0 ? "None selected" : `${count} Class${count > 1 ? 'es' : ''} Selected`;
            }}
          >
            {classes.map((classItem) => (
              <MenuItem key={classItem._id} value={classItem._id}>
                <Checkbox checked={bulkClassIds.indexOf(classItem._id) > -1} />
                <ListItemText
                  primary={classItem.displayName || classItem.name}
                  secondary={`${(classItem.subjects || []).length} subject${(classItem.subjects || []).length === 1 ? '' : 's'} assigned`}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mt: 2 }} size={isMobile ? "small" : "medium"}>
          <InputLabel id="bulk-subjects-checkbox-label">Subjects to Add</InputLabel>
          <Select
            labelId="bulk-subjects-checkbox-label"
            id="bulk-subjects-checkbox"
            multiple
            value={bulkSubjectIds}
            onChange={(e) => {
              const value = e.target.value;
              setBulkSubjectIds(typeof value === 'string' ? value.split(',') : value);
            }}
            input={<OutlinedInput label="Subjects to Add" />}
            renderValue={(selected) => {
              const count = selected.length;
              return count === 0 ? "None selected" : `${count} Subject${count > 1 ? 's' : ''} Selected`;
            }}
          >
            {allSubjects.map((subject) => (
              <MenuItem key={subject._id} value={subject._id}>
                <Checkbox checked={bulkSubjectIds.indexOf(subject._id) > -1} />
                <ListItemText primary={subject.name} secondary={subject.shortName || subject.code} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{
        p: isMobile ? 2 : 3,
        gap: 1,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <Button
          onClick={handleBulkClose}
          variant="outlined"
          disabled={bulkLoading}
          fullWidth={isMobile}
          size={isMobile ? "medium" : "large"}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleBulkAddSubjects}
          disabled={bulkClassIds.length === 0 || bulkSubjectIds.length === 0 || bulkLoading}
          startIcon={bulkLoading ? <CircularProgress size={20} /> : null}
          fullWidth={isMobile}
          size={isMobile ? "medium" : "large"}
        >
          {bulkLoading ? 'Applying...' : 'Apply Subjects'}
        </Button>
      </DialogActions>
    </Dialog>
  ), [
    bulkOpen,
    handleBulkClose,
    isMobile,
    theme,
    bulkError,
    bulkClassIds,
    bulkSubjectIds,
    classes,
    allSubjects,
    bulkLoading,
    handleBulkAddSubjects,
  ]);

  const mergeDialogContent = useMemo(() => {
    const availableSourceOptions =
      selectedBaseLevel === 'CUSTOM'
        ? classes
        : classes.filter((c) => c.name === selectedBaseLevel);

    const availableTargetClasses = classes.filter(
      (c) => !mergeSourceIds.includes(c._id)
    );

    return (
      <Dialog
        open={mergeOpen}
        onClose={handleCloseMerge}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 2 },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#0f172a',
            backgroundImage: 'linear-gradient(135deg, #111c2f 0%, #0f172a 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: isMobile ? 2 : 2.5,
          }}
        >
          <CallMerge fontSize="medium" />
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: 'white', lineHeight: 1.2 }}>
              Merge Class Streams
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Consolidate stream divisions (e.g. Basic 5A + Basic 5B) into a unified class
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          {mergeError && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 1.5 }}>
              {mergeError}
            </Alert>
          )}

          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Step 1: Base Level Selection */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight="700" color="text.primary" gutterBottom>
                1. Select Academic Level to Merge
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Choose a grade level with stream divisions to automatically select its streams.
              </Typography>

              <TextField
                select
                fullWidth
                label="Class Level"
                value={selectedBaseLevel}
                onChange={(e) => handleBaseLevelChange(e.target.value)}
                size={isMobile ? 'small' : 'medium'}
              >
                {streamGroups.map((group) => (
                  <MenuItem key={group.baseName} value={group.baseName}>
                    <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" sx={{ width: '100%' }}>
                      <Typography fontWeight="600">{group.baseName}</Typography>
                      <Stack direction="row" spacing={0.5}>
                        <Chip
                          label={`${group.streamClasses.length} stream${group.streamClasses.length === 1 ? '' : 's'}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={`${group.totalStudents} student${group.totalStudents === 1 ? '' : 's'}`}
                          size="small"
                          variant="filled"
                        />
                      </Stack>
                    </Stack>
                  </MenuItem>
                ))}
                <Divider sx={{ my: 0.5 }} />
                <MenuItem value="CUSTOM">
                  <Typography fontStyle="italic" color="primary">
                    Custom Selection (Choose any source and target classes)
                  </Typography>
                </MenuItem>
              </TextField>
            </Paper>

            {/* Step 2: Source Streams Checklist */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="700" color="text.primary">
                    2. Source Streams to Merge
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Select the stream classes whose students, teachers, and records will be moved.
                  </Typography>
                </Box>
                <Chip
                  label={`${mergeSourceIds.length} Selected`}
                  color={mergeSourceIds.length >= 1 ? 'primary' : 'default'}
                  size="small"
                />
              </Stack>

              {availableSourceOptions.length === 0 ? (
                <Alert severity="info">No classes found for this selection.</Alert>
              ) : (
                <Grid container spacing={1.5}>
                  {availableSourceOptions.map((classItem) => {
                    const isChecked = mergeSourceIds.includes(classItem._id);
                    const studentCount = Array.isArray(classItem.students)
                      ? classItem.students.length
                      : 0;
                    const teacherCount = Array.isArray(classItem.teachers)
                      ? classItem.teachers.length
                      : 0;

                    return (
                      <Grid size={{ xs: 12, sm: 6 }} key={classItem._id}>
                        <Card
                          variant="outlined"
                          onClick={() => handleToggleSourceClass(classItem._id)}
                          sx={{
                            cursor: 'pointer',
                            p: 1.5,
                            borderRadius: 1.5,
                            border: isChecked
                              ? `2px solid ${theme.palette.mode === 'dark' ? '#60a5fa' : '#0f172a'}`
                              : `1px solid ${theme.palette.divider}`,
                            bgcolor: isChecked
                              ? (theme.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.08)' : 'rgba(15, 23, 42, 0.04)')
                              : 'background.paper',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: theme.palette.mode === 'dark' ? '#60a5fa' : '#0f172a',
                            },
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Checkbox
                              checked={isChecked}
                              color="primary"
                              sx={{ p: 0.5 }}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => handleToggleSourceClass(classItem._id)}
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle2" fontWeight="700">
                                {classItem.displayName || classItem.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {studentCount} student{studentCount === 1 ? '' : 's'} • {teacherCount} teacher{teacherCount === 1 ? '' : 's'}
                              </Typography>
                            </Box>
                            {classItem.stream && (
                              <Chip
                                label={`Stream ${classItem.stream}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Paper>

            {/* Step 3: Target Class */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight="700" color="text.primary" gutterBottom>
                3. Destination / Target Class
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Where should all students, subjects, and records be consolidated into?
              </Typography>

              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={targetMode}
                  onChange={(e) => setTargetMode(e.target.value)}
                >
                  {selectedBaseLevel !== 'CUSTOM' && (
                    <FormControlLabel
                      value="base"
                      control={<Radio color="primary" />}
                      label={
                        <Box sx={{ py: 0.5 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" fontWeight="600">
                              Unstreamed Base Class: <strong>{selectedBaseLevel}</strong>
                            </Typography>
                            {mergePreview.targetIsNew ? (
                              <Chip
                                label="Will be auto-created"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                label={`Existing class (${mergePreview.existingTargetStudents} students)`}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Merges into the standard {selectedBaseLevel} without any stream letter.
                          </Typography>
                        </Box>
                      }
                    />
                  )}

                  <FormControlLabel
                    value="custom"
                    control={<Radio color="primary" />}
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight="600">
                          Select an existing class as target
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Merge into one of the streams or another specific class.
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {targetMode === 'custom' && (
                <TextField
                  select
                  fullWidth
                  label="Select Destination Class"
                  value={customTargetId}
                  onChange={(e) => setCustomTargetId(e.target.value)}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ mt: 2 }}
                >
                  <MenuItem value="" disabled>
                    Select destination class
                  </MenuItem>
                  {availableTargetClasses.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Typography fontWeight="500">{c.displayName || c.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(c.students || []).length} students
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Paper>

            {/* Step 4: Live Preview & Summary Card */}
            {mergeSourceIds.length > 0 && (
              <Card
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : '#f8fafc',
                  border: `1.5px solid ${theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.25)' : 'rgba(15, 23, 42, 0.15)'}`,
                  borderRadius: 2,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="700" sx={{ color: theme.palette.mode === 'dark' ? '#93c5fd' : '#0f172a' }} gutterBottom>
                    Merge Preview & Result Summary
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        MOVING FROM ({mergePreview.selectedSourceClasses.length} STREAMS)
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        {mergePreview.selectedSourceClasses.map((c) => (
                          <Chip
                            key={c._id}
                            label={`${c.displayName || c.name} (${c.students?.length || 0})`}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        CONSOLIDATED DESTINATION
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                        {mergePreview.targetDisplayName}
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight="600">
                        {mergePreview.finalStudentCount} Total Students after merge
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        (+{mergePreview.totalStudentsToMove} moved from streams
                        {mergePreview.existingTargetStudents > 0 ? `, ${mergePreview.existingTargetStudents} already enrolled` : ''})
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={`${mergePreview.totalTeachersCount} Teachers Combined`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={`${mergePreview.totalSubjectsCount} Subjects Assigned`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      label="Attendance & SBA Transferred"
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Step 5: Options */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteSourceAfterMerge}
                  onChange={(e) => setDeleteSourceAfterMerge(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="600">
                    Delete source stream classes after successful merge (Recommended)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Removes empty stream classes to prevent duplicate entries in class lists and attendance.
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: isMobile ? 2 : 2.5, gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
          <Button
            onClick={handleCloseMerge}
            variant="outlined"
            disabled={mergeLoading}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'large'}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExecuteMerge}
            disabled={
              mergeSourceIds.length === 0 ||
              mergeLoading ||
              (targetMode === 'custom' && !customTargetId)
            }
            startIcon={mergeLoading ? <CircularProgress size={20} color="inherit" /> : <CallMerge />}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'large'}
            sx={{
              fontWeight: 700,
              bgcolor: '#0f172a',
              color: 'white',
              '&:hover': {
                bgcolor: '#1e293b',
              },
              '&.Mui-disabled': {
                bgcolor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              }
            }}
          >
            {mergeLoading ? 'Merging Streams...' : `Merge ${mergeSourceIds.length} Stream${mergeSourceIds.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [
    mergeOpen,
    handleCloseMerge,
    isMobile,
    theme,
    mergeError,
    selectedBaseLevel,
    streamGroups,
    handleBaseLevelChange,
    classes,
    mergeSourceIds,
    handleToggleSourceClass,
    targetMode,
    mergePreview,
    customTargetId,
    deleteSourceAfterMerge,
    handleExecuteMerge,
    mergeLoading,
  ]);

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {pageHeader}
      {statsCardsContent}
      {classListContent}
      {dialogContent}
      {bulkDialogContent}
      {mergeDialogContent}
    </Container>
  );
};

// 🎯 Export memoized component
export default memo(ClassesPage);
